import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  ListFileAddForm,
  ListFileAddInitialValues,
  ListFileComponents,
  ListFileAdditionalParams,
  ListFileFormOptions,
  onListFileFormSaved,
  IListFileFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateListFile, PermissionEnum, } from 'atlas-shared';
import { createListFile, fetchListFile, getListFileCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useLists, useFields, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateListFile> {}

const ListFileFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const lists = useLists();
  const fields = useFields();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateListFile>>();

  const param_props: IListFileFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    lists: lists.lists,
    fields: fields.fields,
  };
  const formLayout = useMemo(() => ListFileAddForm(values, param_props, t), [values, organizations, lists, fields, t, abilities, ]);
  const options: IFormOptions = useMemo(() => ListFileFormOptions(values || ListFileAddInitialValues, param_props, t), [values, organizations, lists, fields, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateListFile, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateListFile) => save(() => createListFile(values).then(
    res => onSaved ? onSaved(res, navigate) : onListFileFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchListFile(+id)
        .then(({
          organization_id,
          title,
          write_lists,
          clean_lists,
        }) => {
          setCloned({
            organization_id,
            title,
            write_lists,
            clean_lists,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !lists.loaded || !fields.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.List, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={ListFileComponents}
        additionalParams={ListFileAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...ListFileAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('LIST_FILE')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const ListFileFormAddView = React.memo(withAdd(ListFileFormAdd, getListFileCreateSchema, ListFileAddInitialValues));
